/**
 * CollapsiblePanelControl
 * A panel with a labeled header that can be expanded or collapsed.
 * Renders children when expanded.
 */

import React, { useState, memo } from 'react';
import type { FormControl } from '@atlas-forms/types-js';
import { ChevronDownIcon, ChevronUpIcon } from '@atlas-forms/ui-components-react';

export interface CollapsiblePanelControlProps {
  control: FormControl & { children?: FormControl[] };
  renderChild: (child: FormControl) => React.ReactNode;
  mode?: 'edit' | 'view';
}

export const CollapsiblePanelControl = memo(function CollapsiblePanelControl({ control, renderChild, mode = 'edit' }: CollapsiblePanelControlProps) {
  const cfg = (control as any).config ?? {};
  const defaultOpen = cfg.defaultOpen !== false;
  const [open, setOpen] = useState(defaultOpen);
  const children = control.children ?? [];

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    background: 'rgba(51,65,85,0.08)',
    borderRadius: open ? '6px 6px 0 0' : 6,
    border: '1px solid rgba(51,65,85,0.2)',
    cursor: 'pointer',
    userSelect: 'none',
    fontWeight: 600,
    fontSize: 14,
  };

  const bodyStyle: React.CSSProperties = {
    padding: '12px 14px',
    border: '1px solid rgba(51,65,85,0.2)',
    borderTop: 'none',
    borderRadius: '0 0 6px 6px',
  };

  return (
    <div data-control-id={control.id} data-control-type="collapsible-panel">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => mode !== 'view' || cfg.toggleInView ? setOpen((v) => !v) : undefined}
        style={headerStyle}
      >
        <span>{control.label ?? 'Panel'}</span>
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {open ? <ChevronUpIcon width={16} height={16} /> : <ChevronDownIcon width={16} height={16} />}
        </span>
      </button>
      {open && (
        <div style={bodyStyle}>
          {children.length > 0
            ? children.map((child) => (
                <React.Fragment key={child.id}>{renderChild(child)}</React.Fragment>
              ))
            : <span style={{ fontSize: 13, opacity: 0.5 }}>No content</span>}
        </div>
      )}
    </div>
  );
});
